import type {
	CertificationPreview,
	CourseAccreditation,
	CourseAccreditationByProfession,
	CourseCategoryShallow,
} from "@brookbush/types";

import { useAuth } from "@clerk/nextjs";
import clsx from "clsx";
import { motion } from "framer-motion";
import { StarIcon } from "lucide-react";
import { NextSeo } from "next-seo";
import useSWR, { type SWRResponse } from "swr";

import CoursesAccreditationSelect from "./CoursesAccreditationSelect";
import CoursesCard from "./CoursesCard";
import Carousel from "~/components/Carousel";
import LoadingAnimation from "~/components/LoadingAnimation";
import {
	useCertifications,
	useCourseCategories,
	useSelectedCertification,
} from "~/hooks";
import CertificationSelect from "~/pages/certifications/[certificationSlug]/components/CertificationSelect";

interface CoursesOverviewProps {
	shallowCategories: CourseCategoryShallow[];
	accreditationsByProfession: CourseAccreditationByProfession[];
	certifications: CertificationPreview[];
	selectedAccreditation?: CourseAccreditation;
}

const CoursesOverview = ({
	accreditationsByProfession,
	selectedAccreditation,
}: CoursesOverviewProps) => {
	const { userId } = useAuth();

	const { data: categories } = useCourseCategories();

	const { data: passedCredits } = useSWR<number>(
		userId && "/courses/credits/passed",
	);
	const { data: totalCredits } = useSWR<number>(
		"/courses/credits/total",
	) as SWRResponse<number>;

	const { certifications } = useCertifications();

	const { handleSelectCertification, selectedCertification } =
		useSelectedCertification();

	const isSelected = selectedCertification?._id === "courses";

	const coursesTemplate = certifications.find(
		(certification) => certification._id === "courses",
	);

	return (
		<>
			<NextSeo title="Courses For Continuing Education" />

			<motion.div className="flex w-full flex-col gap-4 phablet:gap-6">
				<div className="mt-1 flex laptop:hidden flex-col gap-4">
					<div className="relative flex items-center justify-between gap-5">
						<div className="flex items-center gap-2">
							<button
								className={clsx(
									"-ml-0.5 rounded-full p-1.5 hover:brightness-90 dark:hover:brightness-110",
									isSelected
										? "bg-amber-100 dark:bg-amber-300/20"
										: "bg-slate-100 dark:bg-slate-700",
								)}
								onClick={(e) => {
									e.preventDefault();
									isSelected
										? handleSelectCertification(null)
										: handleSelectCertification(
												coursesTemplate as CertificationPreview,
											);
								}}
							>
								<StarIcon
									size={18}
									className={clsx(
										isSelected
											? "fill-amber-500 text-amber-500"
											: "fill-slate-300 text-slate-300 dark:fill-slate-400 dark:text-slate-400",
									)}
								/>
							</button>

							<CertificationSelect certifications={certifications} />
						</div>

						<div
							className={clsx(
								"relative shrink-0 overflow-hidden rounded-xl border px-3 py-1 font-semibold dark:bg-slate-700",
							)}
						>
							<div className="relative dark:text-slate-50">
								<span className="mr-1">
									{passedCredits} {passedCredits !== undefined && " / "}
									{totalCredits}
								</span>
								Credits
							</div>
						</div>
					</div>

					{accreditationsByProfession && (
						<div className="my-1">
							<CoursesAccreditationSelect
								accreditationsByProfession={accreditationsByProfession}
							/>
						</div>
					)}
				</div>

				<div className="flex flex-col gap-4">
					{selectedAccreditation && (
						<p className="font-medium text-sky-700 dark:text-sky-200">
							Filtered Results For {selectedAccreditation.title}
						</p>
					)}

					{!categories ? (
						<div className="flex h-[75vh] items-center justify-center">
							<LoadingAnimation />
						</div>
					) : !categories.length ? (
						<div className="flex h-[calc(100vh-25rem)] phablet:h-[calc(100vh-12rem)] w-full items-center justify-center">
							<h2 className="font-semibold phablet:text-2xl text-xl">
								No Courses Found
							</h2>
						</div>
					) : (
						<div className="flex w-full flex-col gap-4">
							{categories.map((category) => (
								<Carousel
									title={category.title}
									key={category._id}
									href={`/courses/categories/${category.slug}${
										selectedAccreditation
											? "?accreditation=" + selectedAccreditation.slug
											: ""
									}`}
								>
									{category.courses?.map((course) => (
										<CoursesCard key={course._id} {...course} />
									))}
								</Carousel>
							))}
						</div>
					)}
				</div>
			</motion.div>
		</>
	);
};

export default CoursesOverview;
